import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll
} from 'firebase/storage';
import { db, storage } from '../firebase';

const INVENTORY_COLLECTION = 'inventory';
const INVENTORY_LOGS_COLLECTION = 'inventoryLogs';
const PRODUCTS_COLLECTION = 'products';

// ─── Inventory Items ───

export const getInventoryItems = async () => {
  const snapshot = await getDocs(collection(db, INVENTORY_COLLECTION));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getInventoryByLocation = async (location) => {
  const q = query(
    collection(db, INVENTORY_COLLECTION),
    where('location', '==', location)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getInventoryByProduct = async (productId) => {
  const q = query(
    collection(db, INVENTORY_COLLECTION),
    where('productId', '==', productId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getInventoryItem = async (id) => {
  const docRef = doc(db, INVENTORY_COLLECTION, id);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() };
  }
  return null;
};

export const addInventoryItem = async (itemData, userId) => {
  const newItem = {
    ...itemData,
    quantity: parseInt(itemData.quantity) || 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId || null
  };

  const docRef = await addDoc(collection(db, INVENTORY_COLLECTION), newItem);

  await logInventoryChange({
    inventoryId: docRef.id,
    action: 'added',
    productName: itemData.name,
    location: itemData.location,
    quantity: newItem.quantity,
    userId
  });

  return { id: docRef.id, ...newItem };
};

export const updateInventoryItem = async (id, itemData, userId) => {
  const docRef = doc(db, INVENTORY_COLLECTION, id);
  const existing = await getDoc(docRef);
  const oldData = existing.data();

  const updatedData = {
    ...itemData,
    quantity: parseInt(itemData.quantity) || 0,
    updatedAt: serverTimestamp()
  };

  await updateDoc(docRef, updatedData);

  if (oldData.quantity !== updatedData.quantity) {
    await logInventoryChange({
      inventoryId: id,
      action: 'quantity_changed',
      productName: itemData.name || oldData.name,
      location: itemData.location || oldData.location,
      previousQuantity: oldData.quantity,
      newQuantity: updatedData.quantity,
      userId
    });
  }

  return { id, ...updatedData };
};

export const deleteInventoryItem = async (id, userId) => {
  const docRef = doc(db, INVENTORY_COLLECTION, id);
  const existing = await getDoc(docRef);
  const oldData = existing.data();

  if (oldData?.images?.length > 0) {
    for (const img of oldData.images) {
      if (img.storagePath) {
        try {
          await deleteObject(ref(storage, img.storagePath));
        } catch (e) {
          console.error('Error deleting image from storage:', e);
        }
      }
    }
  }

  await deleteDoc(docRef);

  await logInventoryChange({
    inventoryId: id,
    action: 'deleted',
    productName: oldData?.name,
    location: oldData?.location,
    userId
  });

  return id;
};

// ─── Bulk operations ───

export const transferInventory = async (itemId, fromLocation, toLocation, quantity, userId) => {
  const docRef = doc(db, INVENTORY_COLLECTION, itemId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error('Inventory item not found');

  const data = snap.data();
  if (data.quantity < quantity) throw new Error('Insufficient quantity for transfer');

  const newSourceQty = data.quantity - quantity;
  await updateDoc(docRef, { quantity: newSourceQty, updatedAt: serverTimestamp() });

  const existingAtDest = await getDocs(
    query(
      collection(db, INVENTORY_COLLECTION),
      where('productId', '==', data.productId),
      where('location', '==', toLocation)
    )
  );

  if (!existingAtDest.empty) {
    const destDoc = existingAtDest.docs[0];
    const destData = destDoc.data();
    await updateDoc(destDoc.ref, {
      quantity: destData.quantity + quantity,
      updatedAt: serverTimestamp()
    });
  } else {
    const { ...itemCopy } = data;
    delete itemCopy.createdAt;
    delete itemCopy.updatedAt;
    await addDoc(collection(db, INVENTORY_COLLECTION), {
      ...itemCopy,
      location: toLocation,
      quantity,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId
    });
  }

  await logInventoryChange({
    inventoryId: itemId,
    action: 'transferred',
    productName: data.name,
    location: fromLocation,
    toLocation,
    quantity,
    previousQuantity: data.quantity,
    newQuantity: newSourceQty,
    userId
  });
};

// ─── Image Upload to Firebase Storage ───

export const uploadInventoryImage = async (file, itemId) => {
  const timestamp = Date.now();
  const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `inventory/${itemId}/${timestamp}_${cleanName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return {
    url: downloadURL,
    storagePath,
    fileName: file.name,
    uploadedAt: new Date().toISOString()
  };
};

export const deleteInventoryImage = async (storagePath) => {
  if (!storagePath) return;
  try {
    const imageRef = ref(storage, storagePath);
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting inventory image:', error);
  }
};

export const uploadProductImageToStorage = async (file, productId) => {
  const timestamp = Date.now();
  const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `products/${productId}/${timestamp}_${cleanName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return {
    url: downloadURL,
    storagePath,
    path: storagePath,
    fileName: file.name
  };
};

// ─── Inventory Logs ───

export const logInventoryChange = async (logData) => {
  try {
    await addDoc(collection(db, INVENTORY_LOGS_COLLECTION), {
      ...logData,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error logging inventory change:', error);
  }
};

export const getInventoryLogs = async (inventoryId = null, limit = 50) => {
  let q;
  if (inventoryId) {
    q = query(
      collection(db, INVENTORY_LOGS_COLLECTION),
      where('inventoryId', '==', inventoryId)
    );
  } else {
    q = query(collection(db, INVENTORY_LOGS_COLLECTION));
  }

  const snapshot = await getDocs(q);
  const logs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  logs.sort((a, b) => {
    const aTime = a.timestamp?.toDate?.() || new Date(0);
    const bTime = b.timestamp?.toDate?.() || new Date(0);
    return bTime - aTime;
  });
  return logs.slice(0, limit);
};

// ─── Sync with Products collection ───

export const syncProductToInventory = async (product, userId) => {
  const existingItems = await getInventoryByProduct(product.id);

  if (existingItems.length === 0) {
    const inventoryData = {
      productId: product.id,
      name: product.name,
      description: product.description || '',
      category: product.category || product.categoryId || '',
      location: product.location || 'Both',
      quantity: product.stock || 1,
      condition: 'Good',
      images: product.images || [],
      price: product.price || 0,
      rentalOptions: product.rentalOptions || [],
      features: product.features || [],
      dimensions: product.dimensions || '',
      ageRange: product.ageRange || '',
      capacity: product.capacity || '',
      isAddon: product.isAddon || false,
      status: (product.stock || 1) > 0 ? 'available' : 'unavailable'
    };

    return await addInventoryItem(inventoryData, userId);
  }

  return existingItems[0];
};

export const syncAllProductsToInventory = async (userId) => {
  const productsSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
  const products = productsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

  const results = { synced: 0, skipped: 0, errors: 0 };

  for (const product of products) {
    try {
      const existing = await getInventoryByProduct(product.id);
      if (existing.length === 0) {
        await syncProductToInventory(product, userId);
        results.synced++;
      } else {
        results.skipped++;
      }
    } catch (error) {
      console.error(`Error syncing product ${product.name}:`, error);
      results.errors++;
    }
  }

  return results;
};

// ─── Dashboard stats ───

export const getInventoryStats = async () => {
  const items = await getInventoryItems();

  const totalItems = items.length;
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const byLocation = {};
  const byCategory = {};
  const lowStock = [];

  for (const item of items) {
    const loc = item.location || 'Unassigned';
    byLocation[loc] = (byLocation[loc] || 0) + 1;

    const cat = item.category || 'Uncategorized';
    byCategory[cat] = (byCategory[cat] || 0) + 1;

    if ((item.quantity || 0) <= 1) {
      lowStock.push(item);
    }
  }

  return {
    totalItems,
    totalQuantity,
    byLocation,
    byCategory,
    lowStock,
    locations: Object.keys(byLocation)
  };
};
